import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

// Reusable Area Chart Component with Multiple Y-Axes Support
const AreaChartCard = ({
  title,
  historicalData,
  areas = [],
  lines = [],
  className,
  syncId = null,
  height = 300,
  yDomain = null,
  zeroLine = false,
}) => {
  // Extract unique yAxisIds from areas and lines
  const getYAxisIds = () => {
    const areaAxisIds = areas.map((area) => area.yAxisId).filter(Boolean);
    const lineAxisIds = lines.map((line) => line.yAxisId).filter(Boolean);
    const allAxisIds = [...new Set([...areaAxisIds, ...lineAxisIds])];
    return allAxisIds;
  };

  const yAxisIds = getYAxisIds();
  const hasCustomYAxes = yAxisIds.length > 0;

  return (
    <div className={`bg-white rounded-xl shadow-sm border p-6 ${className}`}>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={historicalData} syncId={syncId}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="timeNumeric"
            stroke="#666"
            fontSize={12}
            angle={-45}
            textAnchor="end"
            height={75}
            domain={["dataMin", "dataMax"]}
            type="number"
            scale="time"
            tickFormatter={(timestamp) => {
              return new Date(timestamp).toLocaleString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              });
            }}
          />

          {/* Default left Y-axis - only render if no custom yAxisIds are used */}
          {!hasCustomYAxes && (
            <YAxis stroke="#666" fontSize={12} orientation="left" {...(yDomain ? { domain: yDomain } : {})} />
          )}

          {/* Custom Y-axes for different metrics */}
          {yAxisIds.map((axisId, index) => (
            <YAxis
              key={axisId}
              yAxisId={axisId}
              stroke="#666"
              fontSize={12}
              orientation={index % 2 === 0 ? "left" : "right"}
              // First custom axis on left, then alternate
            />
          ))}

          <Tooltip
            contentStyle={{
              backgroundColor: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
            }}
            labelFormatter={(timestamp) =>
              new Date(timestamp).toLocaleString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              })
            }
            formatter={(value, name) => [
              typeof value === "number" ? value.toFixed(2) : value,
              name,
            ]}
          />

          {zeroLine && <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="4 3" strokeWidth={1} />}

          {/* Render Areas */}
          {areas.map((area, index) => (
            <Area
              key={index}
              type="monotone"
              dataKey={area.dataKey}
              stroke={area.stroke || "#10b981"}
              fill={area.fill || "#10b981"}
              fillOpacity={area.fillOpacity || 0.3}
              strokeWidth={area.strokeWidth || 2}
              name={area.name || area.dataKey}
              yAxisId={area.yAxisId}
              dot={area.dot || false}
              baseValue={area.baseValue}
            />
          ))}

          {/* Render Lines */}
          {lines?.map((line, index) => (
            <Line
              key={index}
              yAxisId={line.yAxisId}
              type="monotone"
              dataKey={line.dataKey}
              stroke={line.stroke || "#f59e0b"}
              strokeWidth={line.strokeWidth || 2}
              name={line.name || line.dataKey}
              dot={line.dot || false}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default AreaChartCard;
